import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { UiButtonComponent } from '@big-direkt/ui/button';
import { IconBigMediumWinkelUnten, IconComponent } from '@big-direkt/utils/icons';
import { BreakpointService } from '@big-direkt/utils/shared';
import { TranslocoDirective } from '@jsverse/transloco';
import { IsActivePipe } from './is-active.pipe';
import { ResultFilterLabelComponent } from './result-filter-label.component';
import { type ResultFilterModel } from './result-filter.model';

@Component({
    selector: 'big-ui-result-filter',
    templateUrl: './result-filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block',
    },
    imports: [IsActivePipe, TranslocoDirective, UiButtonComponent, IconComponent, ResultFilterLabelComponent],
})
export class ResultFilterComponent {
    private readonly breakpointService = inject(BreakpointService);

    public readonly filter = input<ResultFilterModel[] | null>();
    public readonly canToggleFiltersList = input(true);
    public readonly title = input<string>();
    public readonly hideTitle = input<boolean>();
    public readonly allowMultiple = input(true);
    public readonly variant = input<'dark' | 'light'>('light');

    public readonly activeFilter = output<ResultFilterModel[]>();

    public readonly filteredItems = signal<ResultFilterModel[]>([]);
    public readonly hasOverflow = signal<boolean>(false);
    public readonly iconAngleDown = IconBigMediumWinkelUnten;
    public readonly buttonWrapperHeight = signal<number | undefined>(undefined);
    public readonly showAllFilters = signal<boolean>(false);

    private readonly buttonWrapper = viewChild<ElementRef<Element>>('buttonWrapper');
    private readonly buttonReference = viewChild<ElementRef<Element>>('buttonReference');

    public constructor() {
        effect(() => {
            this.showAllFilters.set(!this.canToggleFiltersList());
        });

        effect(() => {
            const safeguardedValue = this.filter() ?? [];
            this.filteredItems.set(safeguardedValue);

            if (this.getActiveFilters().length) {
                return;
            }

            this.showAllFilters.set(false);
            this.hasOverflow.set(false);
            this.evaluateOverflow();
        });

        effect(() => {
            this.breakpointService.viewPortSize();
            this.buttonWrapper();
            this.buttonReference();

            this.evaluateOverflow();
        });
    }

    public toggleFilter(item: ResultFilterModel): void {
        const newIsActive = !item.isActive;

        if (newIsActive && !this.allowMultiple()) {
            this.getActiveFilters().forEach(activeItem => (activeItem.isActive = false));
        }

        item.isActive = newIsActive;

        this.activeFilter.emit(this.getActiveFilters());
    }

    public clearFilters(): void {
        this.filteredItems().forEach(item => (item.isActive = false));

        this.activeFilter.emit(this.getActiveFilters());
    }

    public toggleFiltersList(): void {
        this.showAllFilters.set(!this.showAllFilters());
    }

    private getActiveFilters(): ResultFilterModel[] {
        return this.filteredItems().filter(item => item.isActive);
    }

    private evaluateOverflow(): void {
        const buttonWrapper = this.buttonWrapper();
        const buttonReference = this.buttonReference();

        if (!buttonWrapper || !buttonReference) {
            return;
        }

        this.hasOverflow.set(buttonWrapper.nativeElement.scrollHeight > buttonReference.nativeElement.clientHeight);
        this.buttonWrapperHeight.set(buttonReference.nativeElement.clientHeight);
    }
}
